import "react-app-polyfill/stable";
import * as ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import {createTheme, ThemeProvider} from "@mui/material";
import {BrowserRouter} from "react-router-dom";
import {subjectColours, typeColours} from "./Util/constants";

declare module "@mui/material/Alert" {
    interface AlertPropsColorOverrides {
      primary: true;
    }
}

// Subject and section colours live in Util/constants and are re-exported here so
// existing imports (`import {subjectColours} from ".."`) keep working.
export {subjectColours, typeColours} from "./Util/constants";

export const theme = createTheme({
    palette: {
        primary: {
            main: "#c8b400",
        },
        secondary: {
            main: "#38383b",
            dark: "#38383b",
        },
        learn: {
            main: typeColours.learn,
            contrastText: typeColours.learn,
        },
        news: {
            main: typeColours.news,
            contrastText: typeColours.news,
        },
        biology: {
            main: subjectColours.biology,
            contrastText: subjectColours.biology,
        },
        chemistry: {
            main: subjectColours.chemistry,
            contrastText: subjectColours.chemistry,
        },
        physics: {
            main: subjectColours.physics,
            contrastText: subjectColours.physics,
        },
        maths: {
            main: subjectColours.maths,
            contrastText: subjectColours.maths,
        },
        white: {
            main: "#ffffff",
        },
    },
    components: {
        MuiTextField: {
            defaultProps: {
                size: "small",
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    width: "100%",
                },
            },
        },
        MuiButton: {
            defaultProps: {
                variant: "outlined",
            },
            styleOverrides: {
                root: {
                    "width": "max-content",
                    "padding": "5px 10px",
                    "&:not(:last-child)": {
                        marginRight: "7px",
                    },
                    "height": "max-content",
                },
                contained: {
                    "color": "#fff",
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    "&:hover": {
                        cursor: "pointer",
                    },
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    height: "15px",
                    background: "rgba(0, 0, 0, 0.25)",
                    borderRadius: "10px",
                },
                bar: {
                    borderRadius: "10px",
                },
            },
        },
        MuiListItemSecondaryAction: {
            styleOverrides: {
                root: {
                    padding: "10px",
                },
            },
        },
        MuiChip: {
            defaultProps: {
                size: "small",
                variant: "outlined",
            },
            styleOverrides: {
                root: {
                    maxWidth: 150,
                },
            },
        },
        MuiFab: {
            defaultProps: {
                color: "primary",
                size: "small",
                ["aria-label"]: "add",
            },
            styleOverrides: {
                root: {
                    position: "absolute",
                    bottom: 15,
                    right: 30,
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    paddingLeft: "13px",
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: "max-content",
                    paddingRight: "13px",
                },
            },
        },
        MuiGrid: {
            defaultProps: {
                spacing: 2,
            },
            styleOverrides: {
                container: {
                    "&.MuiGrid-container": {
                        height: "max-content",
                        marginLeft: "-16px",
                    },
                },
            },
        },
        MuiStack: {
            defaultProps: {
                spacing: 2,
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    borderRadius: 10,
                    boxShadow: "rgba(0, 0, 0, 0.3) 0px 0px 5px 0px",
                    border: "lightgray 1px solid",
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    overflow: "auto",
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    paddingBottom: 0,
                },
                title: {
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                },
            },
        },
        MuiTabs: {
            defaultProps: {
                variant: "scrollable",
                allowScrollButtonsMobile: true,
            },
            styleOverrides: {
                root: {
                    borderBottom: "1px rgba(0,0,0,0.25) solid",
                },
                scrollButtons: {
                    "&.Mui-disabled": {
                        opacity: 0.3,
                    },
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    "&.Mui-selected": {
                        "backgroundColor": "#ffe71d",
                        "borderRadius": 20,
                        "color": "white",
                        ":hover": {
                            backgroundColor: "#5b18b8",
                        },
                        "*": {
                            "color": "white !important",
                        },
                    },
                },
            },
        },
        MuiToggleButtonGroup: {
            defaultProps: {
                size: "small",
            },
        },
        MuiListSubheader: {
            styleOverrides: {
                root: {
                    fontSize: "1.1em",
                    lineHeight: "35px",
                },
            },
            defaultProps: {
                disableSticky: true,
                disableGutters: true,
                color: "primary",
            },
        },
    },
    typography: {
        fontFamily: "century-gothic",
        button: {
            textTransform: "none",
        },
    },
});

declare module "@mui/material/styles/createPalette" {
    interface Palette {
      white: {
        main: string,
    };
    learn?: {
      main: string,
      contrastText: string,
  };
  news?: {
      main: string,
      contrastText: string,
  };
  biology?: {
      main: string,
      contrastText: string,
  };
  chemistry?: {
      main: string,
      contrastText: string,
  };
  physics?: {
      main: string,
      contrastText: string,
  };
  maths?: {
      main: string,
      contrastText: string,
  };
    }

    interface PaletteOptions {
        white?: {
            main: string,
        };
        learn?: {
            main: string,
            contrastText: string,
        };
        news?: {
            main: string,
            contrastText: string,
        };
        biology?: {
            main: string,
            contrastText: string,
        };
        chemistry?: {
            main: string,
            contrastText: string,
        };
        physics?: {
            main: string,
            contrastText: string,
        };
        maths?: {
            main: string,
            contrastText: string,
        };
    }
  }

declare module "@mui/material/Button" {
    interface ButtonPropsColorOverrides {
        white: true;
        learn: true;
        news: true;
        biology: true;
        chemistry: true;
        physics: true;
        maths: true;
    }
}

declare module "@mui/material/Typography" {
    interface TypographyPropsColorOverrides {
        white: true;
        learn: true;
        news: true;
        biology: true;
        chemistry: true;
        physics: true;
        maths: true;
    }
}


declare module "@mui/material/Chip" {
    interface ChipPropsColorOverrides {
        white: true;
        learn: true;
        news: true;
        biology: true;
        chemistry: true;
        physics: true;
        maths: true;
    }
}

const el = document.getElementById("root");

console.log("Render index");

if (el) {
    const root = ReactDOM.createRoot(el);
    root.render(
        <ThemeProvider theme={theme}>
            <BrowserRouter>
                <App />
            </BrowserRouter>
        </ThemeProvider>
    );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
